import { ChevronUp, ChevronDown, Trash2, Plus } from 'lucide-react';
import type { FormField, FieldType, FormOption } from './api.ts';

const TYPES: { value: FieldType; label: string }[] = [
  { value: 'text', label: 'Short text' },
  { value: 'textarea', label: 'Long text' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'file', label: 'File upload' },
];

const newId = (): string => 'f' + Math.random().toString(36).slice(2, 9);

export interface FormValues {
  formEnabled: boolean;
  formFields: FormField[];
  formNoteDv: string; formNoteEn: string;
  formSuccessDv: string; formSuccessEn: string;
}

// Builds a service's application form: enable toggle, note, ordered fields
// (type/label/required/options), and a thank-you message. Emits a patch on any
// change so the parent can autosave.
export function FormBuilder({ values, onChange }: { values: FormValues; onChange: (patch: Partial<FormValues>) => void }): JSX.Element {
  const fields = values.formFields;

  const patchField = (i: number, patch: Partial<FormField>): void => {
    const next = fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f));
    onChange({ formFields: next });
  };
  const addField = (type: FieldType): void => {
    onChange({ formFields: [...fields, { id: newId(), type, labelDv: '', labelEn: '', required: false, options: type === 'select' ? [] : undefined }] });
  };
  const removeField = (i: number): void => onChange({ formFields: fields.filter((_, idx) => idx !== i) });
  const moveField = (i: number, dir: -1 | 1): void => {
    const j = i + dir;
    if (j < 0 || j >= fields.length) return;
    const next = [...fields];
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange({ formFields: next });
  };
  const setOptions = (i: number, options: FormOption[]): void => patchField(i, { options });

  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2.5">
        <button
          type="button" role="switch" aria-checked={values.formEnabled}
          onClick={() => onChange({ formEnabled: !values.formEnabled })}
          className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${values.formEnabled ? 'bg-primary' : 'bg-muted-foreground/30'}`}
        >
          <span className="inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform" style={{ transform: values.formEnabled ? 'translateX(1.05rem)' : 'translateX(0.15rem)' }} />
        </button>
        <span className="text-sm font-medium text-foreground">Enable an online application form</span>
      </label>

      {values.formEnabled && (
        <div className="space-y-4 rounded-lg border border-border bg-background p-3">
          {/* Note */}
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="space-y-1"><label className="status-label">Intro note — Dhivehi</label><textarea dir="rtl" rows={2} className="textarea thaana text-sm" value={values.formNoteDv} onChange={(e) => onChange({ formNoteDv: e.target.value })} /></div>
            <div className="space-y-1"><label className="status-label">Intro note — English</label><textarea dir="ltr" rows={2} className="textarea text-sm" value={values.formNoteEn} onChange={(e) => onChange({ formNoteEn: e.target.value })} /></div>
          </div>

          {/* Fields */}
          <div className="space-y-2">
            {fields.length === 0 && <p className="text-xs text-muted-foreground">No fields yet. Add the questions residents should answer.</p>}
            {fields.map((f, i) => (
              <div key={f.id} className="rounded-lg border border-border bg-paper p-3 space-y-2.5">
                <div className="flex items-center gap-2">
                  <select className="input h-8 w-32 text-xs" value={f.type} onChange={(e) => patchField(i, { type: e.target.value as FieldType, options: e.target.value === 'select' ? (f.options ?? []) : undefined })}>
                    {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <label className="ms-auto inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                    <input type="checkbox" checked={f.required} onChange={(e) => patchField(i, { required: e.target.checked })} className="accent-[hsl(var(--primary))]" /> Required
                  </label>
                  <div className="flex flex-col">
                    <button type="button" aria-label="Move up" disabled={i === 0} onClick={() => moveField(i, -1)} className="text-muted-foreground hover:text-foreground disabled:opacity-20"><ChevronUp size={13} /></button>
                    <button type="button" aria-label="Move down" disabled={i === fields.length - 1} onClick={() => moveField(i, 1)} className="text-muted-foreground hover:text-foreground disabled:opacity-20"><ChevronDown size={13} /></button>
                  </div>
                  <button type="button" aria-label="Delete field" onClick={() => removeField(i)} className="text-muted-foreground hover:text-destructive"><Trash2 size={14} /></button>
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  <input dir="rtl" className="input thaana text-sm" placeholder="ސުވާލު (Dhivehi)" value={f.labelDv} onChange={(e) => patchField(i, { labelDv: e.target.value })} />
                  <input dir="ltr" className="input text-sm" placeholder="Question (English)" value={f.labelEn} onChange={(e) => patchField(i, { labelEn: e.target.value })} />
                </div>
                {f.type === 'select' && (
                  <OptionsEditor options={f.options ?? []} onChange={(opts) => setOptions(i, opts)} />
                )}
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <AddField onAdd={addField} />
          </div>

          {/* Success message */}
          <div className="grid gap-2 sm:grid-cols-2 border-t border-border pt-3">
            <div className="space-y-1"><label className="status-label">Thank-you — Dhivehi</label><input dir="rtl" className="input thaana text-sm" value={values.formSuccessDv} onChange={(e) => onChange({ formSuccessDv: e.target.value })} /></div>
            <div className="space-y-1"><label className="status-label">Thank-you — English</label><input dir="ltr" className="input text-sm" value={values.formSuccessEn} onChange={(e) => onChange({ formSuccessEn: e.target.value })} /></div>
          </div>
        </div>
      )}
    </div>
  );
}

function AddField({ onAdd }: { onAdd: (t: FieldType) => void }): JSX.Element {
  return (
    <div className="inline-flex items-center gap-2">
      <span className="text-xs text-muted-foreground">Add field:</span>
      {TYPES.slice(0, 4).map((t) => (
        <button key={t.value} type="button" onClick={() => onAdd(t.value)} className="btn-secondary btn-sm inline-flex items-center gap-1"><Plus size={12} /> {t.label}</button>
      ))}
      <select className="input h-8 w-28 text-xs" value="" onChange={(e) => { if (e.target.value) onAdd(e.target.value as FieldType); }}>
        <option value="">More…</option>
        {TYPES.slice(4).map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
      </select>
    </div>
  );
}

function OptionsEditor({ options, onChange }: { options: FormOption[]; onChange: (o: FormOption[]) => void }): JSX.Element {
  return (
    <div className="space-y-1.5 rounded-md bg-muted/40 p-2">
      <span className="status-label">Options</span>
      {options.map((o, i) => (
        <div key={i} className="flex items-center gap-2">
          <input dir="rtl" className="input thaana h-8 text-xs" placeholder="ދިވެހި" value={o.dv} onChange={(e) => onChange(options.map((x, idx) => (idx === i ? { ...x, dv: e.target.value } : x)))} />
          <input dir="ltr" className="input h-8 text-xs" placeholder="English" value={o.en} onChange={(e) => onChange(options.map((x, idx) => (idx === i ? { ...x, en: e.target.value } : x)))} />
          <button type="button" aria-label="Remove option" onClick={() => onChange(options.filter((_, idx) => idx !== i))} className="text-muted-foreground hover:text-destructive"><Trash2 size={13} /></button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...options, { dv: '', en: '' }])} className="btn-secondary btn-sm inline-flex items-center gap-1"><Plus size={12} /> Option</button>
    </div>
  );
}
